import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Select from "react-select";
import { Button, Card, Col, Form, Nav, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { arrayMove } from "@dnd-kit/sortable";
import { validateImageDimensions } from "@revbox/helpers-fe"

import { getTableStructure, loadImages } from "../services/micro";
import createNewRecord from "../helpers/create-new-record";
import simpleTopList from "../assets/svg/simple_top_list.svg";
import topListOverview from "../assets/svg/top_list_overview.svg";
import simpleDynamicBanner from "../assets/svg/simple_dynamic_banner.svg";
import dynamic3PackBanner from "../assets/svg/dynamic_3pack_banner.svg";
import getMinCpc from "../services/micro/get_min_cpc";
import validateForRequired from "../helpers/validateForRequired";
import { getParentPublisherInventories } from "../services/pub";
import { FileUploadInput } from "../adv/campaign/components/common/FileUploadInput";
import ImageCropModal from "../adv/campaign/components/common/ImageCropModal";
import MultipleFilesUploadInput from "../adv/campaign/components/common/MultipleFilesUploadInput";
import { useCookiesContext } from "../contexts/CookiesContext";
import { validateEmailAndUrl } from "@revbox/helpers-fe";

import { getPublisherCategories } from "../helpers/get-publisher-categories";
import { getAllPubCampaings } from "../services/pub";
import { useDebouncedCallback } from "../hooks/useDebounceCallback";
import { validateEmail } from "../helpers/validate-email-onboarding";
import ModalOnboard from "./onboarding/ModalOnboard";
import { validateApplication } from "../services/pub/create_new_applications";
import LoaderWithBlur from "./LoaderWithBlur";
import IntervalAuctionSettings from "./Form/IntervalAuctionSettings";
import { ALL_CATEGORIES, handleMultiselectorChange } from "./Form/helpers";
import FormMultiselector from "./Form/FormMultiselector";

const PUBLISHER_INVENTORY = "publisher_inventory";
const TRANSACTIONS = "transactions";
const INVENTORY_CAMPAIGN = "inventory_campaigns";

const BALANCE_CREATION_TAB = "balance_creation";
const MANUAL_CREATION_TAB = "manual_creation";
const DEFAULT_CREATION_TAB = "default_creation";

const redirectAfterSubmit = (redirect) => {
  window.location.href = redirect;
};

export default function RecordCreate({
  table_name,
  title,
  redirect,
  breadcrumb,
  item_name,
}) {
  const [tableStructure, setTableStructure] = useState({});
  const [columnsValues, setColumnsValues] = useState({});
  const [parentInventories, setParentInventories] = useState([]);
  const [parentInventory, setParentInventory] = useState(null);
  const [data, setData] = useState({
    website_id: null,
    inventory_id: null,
    campaign_id: null,
  });
  const [minCpcValue, setMinCpcValue] = useState();
  const [boxTypes, setBoxTypes] = useState([]);
  const [validationResults, setValidationResults] = useState({});
  const [isInventoryTypeCluster, setIsInventoryTypeCluster] = useState(false);
  const [isInventoryTypeHomepage, setIsInventoryTypeHomepage] = useState(false);
  const [isInventoryTypeEmail, setIsInventoryTypeEmail] = useState(false);
  const user = useSelector((state) => state.user);
  const publisher_id = user.publisher_id;
  const [modalShow, setModalShow] = useState(false);
  const [imageFiles, setImageFiles] = useState(null);
  const logoInputRef = useRef(null);
  const screenshotsInputRef = useRef(null);
  const [croppedLogoPreview, setCroppedLogoPreview] = useState(null);
  const [croppedScreenshotsPreviews, setCroppedScreenshotsPreviews] = useState(
    []
  );
  const [croppedLogoFile, setCroppedLogoFile] = useState(null);
  const [croppedScreenshotFiles, setCroppedScreenshotFiles] = useState([]);
  const [ref, setRef] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 150, height: 150 });
  const [tab, setTab] = useState(MANUAL_CREATION_TAB);
  const [campaigns, setCampaigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [validationModalMessage, setValidationModalMessage] = useState('');
  const { cookiesData, getPathKey, getToken, getPublisherId } = useCookiesContext();

  const [isRecordSubmitting, setIsRecordSubmitting] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState(false);

  useEffect(() => {
    getTableStructure(table_name, publisher_id).then((res) => {
      setTableStructure(res);

      const columns = res.columns.reduce((acc, column) => {
        if (column.default_value) {
          acc[column.name] = column.default_value;
        } else {
          acc[column.name] = "";
        }
        return acc;
      }, {});

      setColumnsValues(columns);

      getParentPublisherInventories().then((d) => {
        if (d.success) {
          // add default inventory None
          d.data.unshift({
            id: 0,
            name: "None",
            value: null,
          });
          setParentInventories(d.data);
        }
      });

      getAllPubCampaings().then((d) => {
        if (d.success) {
          setCampaigns(d.data);
        }
      });
    });

    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    updateCpcValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const onSubmit = async (e) => {
    try {
      if (table_name === 'client_applications' && invalidEmail) {
        toast.error('Invalid Email. Please use a business email address')
        return
      }
  
      let submitColumnsValues = { ...columnsValues }

      if (table_name === PUBLISHER_INVENTORY) {
        submitColumnsValues.min_cpc = minCpcValue
      }

      submitColumnsValues.publisher_id = publisher_id
      submitColumnsValues.box_types = submitColumnsValues?.box_types || []
      submitColumnsValues.related_categories_ids = submitColumnsValues?.related_categories_ids || []
      submitColumnsValues.cms_users = submitColumnsValues?.cms_users || []
      submitColumnsValues.website_id = submitColumnsValues.website_id?.value || submitColumnsValues.website_id
  
      await Promise.all(
        Object.keys(submitColumnsValues).map(async key => {
          const col = tableStructure?.columns?.find(column => column.name === key)
          if (col?.type === 'date') {
            submitColumnsValues[key] = new Date(submitColumnsValues[key]).getTime()
          }
  
          if (col?.type === 'string' || col?.type === 'text') {
            submitColumnsValues[key] = String(submitColumnsValues[key])
          }
  
          if (col?.type === 'number') {
            submitColumnsValues[key] = Number(submitColumnsValues[key])
          }
  
          if (col?.type === 'boolean') {
            submitColumnsValues[key] = Boolean(submitColumnsValues[key])
          }
  
          if (col?.type === 'list') {
            submitColumnsValues[key] = submitColumnsValues[key].split(',').map(item => item.trim())
          }
  
          if (col?.type === 'selector') {
            // submitColumnsValues[key] = submitColumnsValues[key].value;
  
            const value =
              col?.options?.find(option => option.label === submitColumnsValues[key].label)?.value ??
              col?.options?.find(option => option.value === submitColumnsValues[key])?.value
  
            submitColumnsValues[key] = value
          }
  
          if (col?.type === 'multiselector') {
            if (
              key === 'related_categories_ids' &&
              (isInventoryTypeHomepage ||
                isInventoryTypeEmail ||
                ([6,7, 8, 9].includes(columnsValues['inventory_type']?.value) && submitColumnsValues[key]?.find(value => value.value === ALL_CATEGORIES.value)))
            ) {
              const allValues = col?.options?.map(option => option.value)
  
              submitColumnsValues[key] = allValues
              return
            }
  
            if (submitColumnsValues[key]?.length) {
              // const values = submitColumnsValues[key]?.map(value => {
              //   console.log('value:', value)
              //   console.log('col?.options find:', col?.options.find(option => option.label === value.))
              //   return col?.options.find(option => option.label === value.label)?.value
              // })
              const values = submitColumnsValues[key].map((item) => item.value )
              submitColumnsValues[key] = values
            } else {
              submitColumnsValues[key] = []
            }
          }
  
          if (col?.type === 'files') {
            if (croppedScreenshotFiles.length > 0) {
              const screenshotIds = []
              for (const croppedFile of croppedScreenshotFiles) {
                if (typeof croppedFile !== 'number') {
                  const screenshotResponse = await loadImages([croppedFile])
                  if (screenshotResponse?.success) {
                    screenshotIds.push(Number(screenshotResponse.data[0].id))
                  }
                } else {
                  screenshotIds.push(croppedFile)
                }
              }
              submitColumnsValues[key] = screenshotIds
            } else {
              submitColumnsValues[key] = []
            }
          }
  
          if (col?.type === 'file') {
            if (croppedLogoFile) {
              const logoResponse = await loadImages([croppedLogoFile])
              if (logoResponse?.success) {
                submitColumnsValues[key] = Number(logoResponse.data[0].id)
              }
            } else {
              submitColumnsValues[key] = null
            }
          }
        })
      )
  
      let newRecord = tableStructure.create_columns.reduce((acc, column) => {
        acc[column] = submitColumnsValues[column]
        return acc
      }, {})
  
      const isManualCreation = table_name === TRANSACTIONS && tab === MANUAL_CREATION_TAB
      const isBalanceCreation = table_name === TRANSACTIONS && tab === BALANCE_CREATION_TAB
      const isValid = await validateForRequired(tableStructure, newRecord, isManualCreation, isBalanceCreation)
  
      if (isValid.vat_number === false) {
        toast.error('VAT number is not valid!')
      }
  
      setValidationResults(isValid)
  
      if (table_name === PUBLISHER_INVENTORY && parentInventory) {
        const newParentInventory = parentInventories.find(item => item.id === parentInventory.value)
        newRecord.publisher_inventory_parent_id = newParentInventory.id
        const newHirarchyLevel = newParentInventory.inventory_hirarchy_level + 1
        newRecord.inventory_hirarchy_level = newHirarchyLevel
      }
  
      if (table_name === 'client_applications') {
        // upd: 06.10 Nick said close it.
        // const isEmailMatchDomain = validateEmailAndUrl(newRecord.email, newRecord.product_url)
        // if (!isEmailMatchDomain.valid) {
        //   toast.error(isEmailMatchDomain.error)
        //   return
        // }
  
        const validationResult = await validateApplication(newRecord)
  
        if (!validationResult.success) {
          if (validationResult?.modalMessage) {
            setValidationModalMessage(validationResult.modalMessage)
          } else if (validationResult.message) {
            toast.error(validationResult.message)
          }
          return
        }
      }
  
      if (Object.values(isValid).every(result => result)) {
        toast.success('Record creating...')
  
        if (isBalanceCreation) {
          newRecord.transaction_type_id = 1
          newRecord.transaction_status_id = 1
          newRecord.balance_correction = true
        }

        if (isManualCreation) {
          newRecord.transaction_type_id = 1
          newRecord.transaction_status_id = 1
          newRecord.manual_correction = true;
        }
  
        setIsRecordSubmitting(true)
        setTimeout(() => {
          createNewRecord(tableStructure.db_name, newRecord)
            .then(res => {
              if (res?.success) {
                toast.success('Record created successfully!')
                redirectAfterSubmit(redirect)
              } else {
                toast.error('Record creating failed!')
              }
            })
            .catch(err => {
              console.log('ERROR', err)
              if (err?.message) {
                toast.error(err.message)
              } else {
                toast.error("You don't have permission to create this record!")
              }
            })
            .finally(() => {
              setIsRecordSubmitting(false)
            })
        }, 3000)
      }
    } catch (error) {
      console.log('error', error)
    }
  };

  const handleParentInventoryChange = (e) => {
    setParentInventory(e);
  };

  const handleOnblur = (e, column) => {
    e.target.value = roundToMinIncrement(e, column);
  };

  const insertMinCpcOrDefaultValue = (column) => {
    if (table_name === TRANSACTIONS) {
      return columnsValues[column.name]

    }

    if (
      columnsValues.hasOwnProperty(column.name) &&
      columnsValues[column.name] !== "" &&
      Number(columnsValues[column.name]) >= 0
    ) {
      return columnsValues[column.name];

    }

    if ((column.name === "min_cpc" || column.name === "cpc") && minCpcValue && Number(minCpcValue) >= 0) {
      return Number(minCpcValue)
    }

    return "";
  };
  const handleRemoveScreenshot = (index) => {
    setCroppedScreenshotsPreviews((prevPreviews) => {
      const updatedPreviews = prevPreviews.filter((_, i) => i !== index);

      return updatedPreviews;
    });

    const newFiles = croppedScreenshotFiles.filter((_, i) => i !== index);

    if (newFiles.length === 0) {
      document.getElementById("screenshots-input-placeholder").innerText =
        "No files selected";
    }

    setCroppedScreenshotFiles(newFiles);
  };

  // If column have min_increment property, it will be rounded, else- return current value
  const roundToMinIncrement = (e, column) =>
    column.min_increment
      ? Math.round(Number(e.target.value) / column.min_increment) *
      column.min_increment
      : Number(e.target.value);

  const debouncedValidate = useDebouncedCallback((value) => {
    const { validationResults, invalidEmail } = validateEmail(value);
    setValidationResults(prev => ({ ...prev, ...validationResults }));
    if (!invalidEmail) {
      setInvalidEmail(false);
    }
  }, 300);

  const handleColumnChange = async (e, column) => {
    let columnForChange = { ...columnsValues };

    if (column.type === 'boolean') {
        columnForChange[column.name] = e.target.checked
    } else if (column.type === 'string') {
        columnForChange[column.name] = String(e.target.value)
    } else if (column.type === 'text') {
        columnForChange[column.name] = String(e.target.value)
        if (table_name === 'client_applications' && column.name === 'email') {
            debouncedValidate(e.target.value)
        }
    } else if (column.type === 'date') {
        columnForChange[column.name] = new Date(e.target.value).getTime()
    } else if (column.type === 'number') {
        if (table_name === TRANSACTIONS) {
            const value = e.target.value
            columnForChange[column.name] = value
        } else {
            columnForChange[column.name] = roundToMinIncrement(e, column)
        }
    } else if (column.type === 'list') {
        columnForChange[column.name] = e.target.value.split(',').map(item => item.trim())
    } else if (column.type === 'selector') {
        if (column.name === 'inventory_type' && e.value === 3) {
            setIsInventoryTypeHomepage(true)
        } else if (column.name === 'inventory_type' && e.value !== 3) {
            setIsInventoryTypeHomepage(false)
        }

        if (column.name === 'inventory_type' && e.value === 35) {
            setIsInventoryTypeEmail(true)
        } else if (column.name === 'inventory_type' && e.value !== 35) {
            setIsInventoryTypeEmail(false)
        }

        if (column.name === 'inventory_type' && e.value === 5) {
            setIsInventoryTypeCluster(true)
        } else if (column.name === 'inventory_type' && e.value !== 5) {
            setIsInventoryTypeCluster(false)
        } else if (column.name === 'main_category') {
            columnForChange[column.name] = e.label
        }

        columnForChange[column.name] = e.label ? e : null
    } else if (column.type === 'files') {
        const files = e.target.files
        const totalScreenshots = croppedScreenshotFiles.length + files.length

        if (files.length > 0 && totalScreenshots <= 4) {
            const newFiles = Array.from(files)
            const screenshotsDimensions = { width: 1600, height: 900 }
            const { validFiles } = await validateImageDimensions(newFiles, screenshotsDimensions)

            if (validFiles.length > 0) {
                setDimensions(screenshotsDimensions)
                setRef(screenshotsInputRef)
                setImageFiles(validFiles)
                setModalShow(true)
            }

            e.target.value = null
        } else if (totalScreenshots > 4) {
            e.target.value = null
            toast.error('You can upload a maximum of 4 files in total!')
        } else {
            e.target.value = null
        }
    } else if (column.type === 'file') {
        if (e.target.files.length > 0) {
            const files = Array.from(e.target.files)
            const logoDimensions = { width: 150, height: 150 }

            const { validFiles } = await validateImageDimensions(files, logoDimensions)

            if (validFiles.length > 0) {
                setRef(logoInputRef)
                setImageFiles(validFiles)
                setDimensions(logoDimensions)
                setModalShow(true)
            }

            e.target.value = null
        } else columnForChange[column.name] = null
    } else if (column.type === 'multiselector') {
        const { columnName, values } = handleMultiselectorChange(e, columnsValues, column.name)
        columnForChange[columnName] = values
        if (columnName === 'box_types') {
            setBoxTypes(values.map((item) => item.value))
        }
        
    } else {
        columnForChange[column.name] = e.target.value
    }

    setColumnsValues(columnForChange);

    if (column.name === "inventory_id") {
      setData({ ...data, inventory_id: e });
    }
    if (column.name === "campaign_id") {
      setData({ ...data, campaign_id: e });
    }
    if (column.name === "website_id") {
      setData({ ...data, website_id: e });
    }

    // const newTableColumnsStructure = tableStructure.columns.map((column) => {
    //   column.value = columnForChange[column.name];
    //   return column;
    // });

    // setTableStructure({ ...tableStructure, columns: newTableColumnsStructure });
  };

  async function updateCpcValue() {
    const newMinCpcValue = await getMinCpc(
      table_name,
      data.website_id?.value,
      data.inventory_id?.value,
      data.campaign_id?.value
    );
    setMinCpcValue(newMinCpcValue?.data);
  }

  const modalHandleClose = () => setModalShow(false);

  const handleSaveCroppedImages = (croppedFile) => {
    const objectUrl = URL.createObjectURL(croppedFile);

    if (ref === logoInputRef) {
      setCroppedLogoFile(croppedFile);
      setCroppedLogoPreview(objectUrl);
      document.getElementById("logo-input-placeholder").innerText =
        "Logo loaded";
    } else if (ref === screenshotsInputRef) {
      setCroppedScreenshotsPreviews((prevPreviews) => {
        const updatedPreviews = [...prevPreviews, objectUrl];

        return updatedPreviews;
      });

      setCroppedScreenshotFiles((prevFiles) => {
        const updatedFiles = [...prevFiles, croppedFile];

        return updatedFiles;
      });

      document.getElementById("screenshots-input-placeholder").innerText =
        "Screenshots loaded";
    }
  };

  const handleDragEnd = (event) => {
    if (!croppedScreenshotsPreviews.length) {
      return;
    }

    const { active, over } = event;
    const oldIndex = croppedScreenshotsPreviews.indexOf(active?.id);
    const newIndex = croppedScreenshotsPreviews.indexOf(over?.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newPreviews = arrayMove(
      croppedScreenshotsPreviews,
      oldIndex,
      newIndex
    );

    const newFiles = arrayMove(croppedScreenshotFiles, oldIndex, newIndex);
    setCroppedScreenshotsPreviews(newPreviews);
    setCroppedScreenshotFiles(newFiles);
  };

  const changeTab = (tab) => {
    setTab(tab);
  };

  const isAccountIdSelected = useMemo(() => {
    return columnsValues?.account_id && columnsValues?.account_id !== "";
  }, [columnsValues]);

  const fetchCategories = useCallback(async () => {
    try {
      const token = getToken();
      const publisherId = getPublisherId();
      const publisherIdFromParams = new URLSearchParams(window.location.search).get("pub");
      const pubId = publisherIdFromParams || publisherId;
      const response = await getPublisherCategories(token, pubId);
      if (response?.success) {
        const categories = response.data.map((category) => ({
          label: category.name,
          value: category.id,
        }));

        setCategories(categories);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to load categories");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookiesData, getPathKey]);

  return (
    <React.Fragment>
      {modalShow && (
        <ImageCropModal
          modalShow={modalShow}
          modalHandleClose={modalHandleClose}
          imageFiles={imageFiles}
          onSave={handleSaveCroppedImages}
          dimensions={dimensions}
          ref={ref}
        />
      )}
      {validationModalMessage && (
        <ModalOnboard isModalOpen={true} onModalClose={() => setValidationModalMessage('')} modalStatus={validationModalMessage.includes('product name') ? 'declined_product_name' : 'declined_company_name'} modalMessage={validationModalMessage} />
      )}
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <Nav.Item>
                <Nav.Link
                  className="text-primary"
                  href="/pub/dashboard/revenue"
                  to="/pub/dashboard/revenue"
                >
                  PUB Home
                </Nav.Link>
              </Nav.Item>
              <span className="mx-2">/</span>
              <Nav.Item>
                <Nav.Link className="text-primary" href={redirect}>
                  {breadcrumb}
                </Nav.Link>
              </Nav.Item>
              <span className="mx-2">/</span>
              <Nav.Item>
                <Nav.Link href="#">New</Nav.Link>
              </Nav.Item>
            </ol>
            <h2 className="main-title mb-0">{title}</h2>
          </div>
        </div>

        <Row className="g-3 justify-content-center">
          {table_name === TRANSACTIONS && (
            <Nav className="nav-line mb-0 ml-4 mt-5">
              {
                <Nav.Link
                  href="#manual-correction"
                  className={tab === MANUAL_CREATION_TAB ? "active" : ""}
                  onClick={() => changeTab(MANUAL_CREATION_TAB)}
                >
                  Credits
                </Nav.Link>
              }
              {
                <Nav.Link
                  href="#balance-correction"
                  className={tab === BALANCE_CREATION_TAB ? "active" : ""}
                  onClick={() => changeTab(BALANCE_CREATION_TAB)}
                >
                  Balance
                </Nav.Link>
              }
              {
                <Nav.Link
                  href="#default-creation"
                  className={tab === DEFAULT_CREATION_TAB ? "active" : ""}
                  onClick={() => changeTab(DEFAULT_CREATION_TAB)}
                >
                  Invoice
                </Nav.Link>
              }
            </Nav>
          )}
          <Col md="12">
            <Row>
              <Col sm="12" md="6">
                <Card className="card-settings mt-2">
                  <Card.Body className="p-0">
                    {tableStructure &&
                      tableStructure.columns &&
                      tableStructure.columns.map((column, index) => {
                        if (column.name === "client_application_status") {
                          return <></>
                        }

                        if (['close_time', 'recurring_interval'].includes(column.name)) {
                          return <></>
                        }
                        if (['forecasting'].includes(column.name) && table_name !== PUBLISHER_INVENTORY) {
                          return <></>
                        }

                        if (
                          tableStructure.create_columns.find(
                            (create_column) => create_column === column.name
                          )
                        ) {
                          if (
                            table_name === TRANSACTIONS &&
                            tab === MANUAL_CREATION_TAB &&
                            (column.name === "user_email" ||
                              column.name === "first_name" ||
                              column.name === "last_name" ||
                              column.name === "invoice_account_name" ||
                              column.name === "invoice_address" ||
                              column.name === "invoice_street" ||
                              column.name === "invoice_city" ||
                              column.name === "invoice_address_zip_code" ||
                              column.name === "invoice_address_state" ||
                              column.name === "invoice_address_country_id" ||
                              column.name === "vat_number" ||
                              column.name === "transaction_type_id" ||
                              column.name === "transaction_status_id")
                          ) {
                            return <></>;
                          }

                          if (
                            table_name === TRANSACTIONS &&
                            tab === BALANCE_CREATION_TAB &&
                            (column.name === "user_email" ||
                              column.name === "first_name" ||
                              column.name === "last_name" ||
                              column.name === "invoice_account_name" ||
                              column.name === "invoice_address" ||
                              column.name === "invoice_street" ||
                              column.name === "invoice_city" ||
                              column.name === "invoice_address_zip_code" ||
                              column.name === "invoice_address_state" ||
                              column.name === "invoice_address_country_id" ||
                              column.name === "vat_number" ||
                              column.name === "is_free" ||
                              column.name === "transaction_type_id" ||
                              column.name === "transaction_status_id")
                          ) {
                            return <></>;
                          }

                          switch (column.type) {
                            case "text":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>{column.is_required && "Required"}</p>
                                    </Col>
                                    <Col md>
                                      <Form.Control
                                        type="text"
                                        placeholder={column.placeholder}
                                        onChange={(e) =>
                                          handleColumnChange(e, column)
                                        }
                                        value={columnsValues[column.name]}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "string":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>{column.is_required && "Required"}</p>
                                    </Col>
                                    <Col md>
                                      <Form.Control
                                        type="text"
                                        placeholder={column.placeholder}
                                        onChange={(e) =>
                                          handleColumnChange(e, column)
                                        }
                                        value={columnsValues[column.name]}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "list":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>
                                        {column.title} (separated by comma)
                                      </h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <Col md>
                                      <Form.Control
                                        type="text"
                                        placeholder={column.placeholder}
                                        onChange={(e) =>
                                          handleColumnChange(e, column)
                                        }
                                        value={columnsValues[column.name]}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "file":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <FileUploadInput
                                      onLogoChange={(e) =>
                                        handleColumnChange(e, column)
                                      }
                                      logoInputRef={logoInputRef}
                                      croppedLogoPreview={croppedLogoPreview}
                                    />
                                  </Row>
                                </div>
                              );
                            case "files":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <MultipleFilesUploadInput
                                      onScreenshotsChange={(e) =>
                                        handleColumnChange(e, column)
                                      }
                                      screenshotsInputRef={screenshotsInputRef}
                                      croppedScreenshotsPreviews={
                                        croppedScreenshotsPreviews
                                      }
                                      handleRemoveScreenshot={
                                        handleRemoveScreenshot
                                      }
                                      handleDragEnd={handleDragEnd}
                                    />
                                  </Row>
                                </div>
                              );
                            case "boolean":
                              if (column.name === 'review_required') {
                                return (
                                <div className="setting-item">
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                    </Col>
                                    <Col md>
                                      <Form.Check
                                        type="switch"
                                        onChange={e => {
                                          handleColumnChange(
                                            e,
                                            {
                                              name: 'review_required',
                                              title: column.title,
                                              placeholder: column.title,
                                              type: 'boolean'
                                            }
                                          )
                                        }}
                                        checked={columnsValues['review_required'] === true}
                                      />
                                    </Col>
                                  </Row>
                                </div>
                                )
                              }
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <Col md>
                                      <Form.Check
                                        type="switch"
                                        label={column.placeholder}
                                        onChange={(e) =>
                                          handleColumnChange(e, column)
                                        }
                                        checked={columnsValues[column.name]}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "date":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <Col md>
                                      <Form.Control
                                        type="date"
                                        placeholder={column.placeholder}
                                        onChange={(e) =>
                                          handleColumnChange(e, column)
                                        }
                                        value={columnsValues[column.name]}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "number":
                              const interval_auction = table_name === PUBLISHER_INVENTORY && [6,8,9].includes(columnsValues["inventory_type"]?.value)
                              const getStep = () => {
                                if (interval_auction) {
                                  return 10
                                }
                                if (column.min_increment) {
                                  return column.min_increment
                                }
                                return 1
                              }
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{interval_auction ? "Min Bid" : column.title}</h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <Col md>
                                      <Form.Control
                                        type="number"
                                        placeholder={column.placeholder}
                                        onChange={(e) =>
                                          handleColumnChange(e, column)
                                        }
                                        onBlur={(e) => handleOnblur(e, column)}
                                        value={insertMinCpcOrDefaultValue(
                                          column
                                        )}
                                        min={interval_auction ? 100 : undefined}
                                        step={getStep()}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "selector":
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>{column.is_required && "Required"}</p>
                                    </Col>
                                    <Col md>
                                      { // Campaign selector info
                                        (
                                          column.name === "campaign_id" &&
                                          table_name === INVENTORY_CAMPAIGN
                                        ) &&
                                        <Select
                                          id="basic-select-single"
                                          onChange={(e) => {
                                            handleColumnChange(e, column);
                                          }}
                                          options={campaigns.map((option) => ({
                                            label: option.name,
                                            value: option.id,
                                          }))}
                                          placeholder={column.placeholder}
                                          is
                                          value={[columnsValues[column.name]]}
                                        />
                                      }
                                      { // Campaign selector info
                                        (
                                          column.name === "campaign_id" &&
                                          table_name === TRANSACTIONS &&
                                          !isAccountIdSelected
                                        ) &&
                                        <Select
                                          id={`basic-select-single`}
                                          value={{
                                            label:
                                              "Please select account first",
                                            value: "",
                                          }}
                                          isDisabled={true}
                                        />
                                      }
                                      { // Campaign selector
                                        (
                                          column.name === "campaign_id" &&
                                          table_name === TRANSACTIONS &&
                                          isAccountIdSelected
                                        ) &&
                                        <Select
                                          id="basic-select-single"
                                          onChange={(e) => {
                                            handleColumnChange(e, column);
                                          }}
                                          options={campaigns.filter(campaign => {
                                            return campaign.account_id == columnsValues["account_id"].value
                                          }).map((option) => ({
                                            label: option.name,
                                            value: option.id,
                                          }))}
                                          placeholder={column.placeholder}
                                          is
                                          value={[columnsValues[column.name]]}
                                        />
                                      }
                                      { // Website selector
                                        column.name === "website_id" && isInventoryTypeCluster &&
                                        <Select
                                          id={`basic-select-single`}
                                          value={{
                                            label: "All websites",
                                            value:
                                              "All websites are associated to inventory by default",
                                          }}
                                          isDisabled={true}
                                        />
                                      }
                                      { // Website selector info
                                        column.name === "website_id" && isInventoryTypeCluster ?
                                          <div className="d-flex align-items-center fs-xs text-secondary mb-0 mt-1">
                                            <i className="ri-information-line me-1"></i>
                                            All websites are associated to inventory by default
                                          </div>
                                          :
                                          <></>
                                      }
                                      { // Any other selector
                                        (
                                          !isInventoryTypeCluster &&
                                          // column.name !== "website_id" &&
                                          (
                                            column.name !== "campaign_id" &&
                                            table_name !== TRANSACTIONS
                                          )
                                        ) &&
                                        (
                                          <Select
                                            id="basic-select-single"
                                            onChange={(e) => {
                                              handleColumnChange(e, column)
                                            }
                                            }
                                            options={column.options.map((option) => ({
                                              label: option.label,
                                              value: option.value,
                                            }))}
                                            placeholder={column.placeholder}
                                            is
                                            value={[columnsValues[column.name]]}
                                          />
                                        )
                                      }
                                      { // Any other selector
                                        (
                                          isInventoryTypeCluster &&
                                          column.name !== "website_id" &&
                                          (
                                            column.name !== "campaign_id" &&
                                            table_name !== TRANSACTIONS
                                          )
                                        ) &&
                                        (
                                          <Select
                                            id="basic-select-single"
                                            onChange={(e) => {
                                              handleColumnChange(e, column)
                                            }
                                            }
                                            options={column.options.map((option) => ({
                                              label: option.label,
                                              value: option.value,
                                            }))}
                                            placeholder={column.placeholder}
                                            is
                                            value={[columnsValues[column.name]]}
                                          />
                                        )
                                      }
                                      { // Any other selector
                                        (
                                          (
                                            column.name !== "campaign_id" &&
                                            table_name === TRANSACTIONS
                                          )
                                        ) &&
                                        (
                                          <Select
                                            id="basic-select-single"
                                            onChange={(e) => {
                                              handleColumnChange(e, column);
                                            }}
                                            options={column.options.map(
                                              (option) => ({
                                                label: option.label,
                                                value: option.value,
                                              })
                                            )}
                                            placeholder={column.placeholder}
                                            is
                                            value={[columnsValues[column.name]]}
                                          />
                                        )}
                                    </Col>
                                  </Row>
                                </div>
                              );
                            case "multiselector":
                              return (
                                <FormMultiselector
                                  column={column}
                                  index={index}
                                  columnsValues={columnsValues}
                                  handleColumnChange={handleColumnChange}
                                />
                              );
                            default:
                              return (
                                <div className="setting-item" key={index}>
                                  <Row className="g-2 align-items-center">
                                    <Col md="3">
                                      <h6>{column.title}</h6>
                                      <p>
                                        {column.is_required ? "Required" : ""}
                                      </p>
                                    </Col>
                                    <Col md>
                                      <Form.Control
                                        type="text"
                                        placeholder={column.placeholder}
                                        isInvalid={
                                          validationResults[column.name] ===
                                          false
                                        }
                                      />
                                    </Col>
                                  </Row>
                                </div>
                              );
                          }
                        } else {
                          return <></>;
                        }
                      })}

                    {table_name === PUBLISHER_INVENTORY &&
                      columnsValues["inventory_type"]?.value === 5 && (
                        <div className="setting-item">
                          <Row className="g-2 align-items-center">
                            <Col md="3">
                              <h6>Parent Inventory</h6>
                              <p>
                                Specify the parent inventory for this inventory
                              </p>
                            </Col>
                            <Col md>
                              <Select
                                id="basic-select-single-parent-inventory"
                                onChange={(e) => handleParentInventoryChange(e)}
                                options={parentInventories.map((option) => ({
                                  label: option.name,
                                  value: option.id,
                                }))}
                                placeholder="Select Parent Inventory"
                                value={parentInventory}
                              />
                            </Col>
                          </Row>
                        </div>
                      )}

{/* close_time, switch is recurring interval, if is true, show recurring interval dropdown */}
                    {table_name === PUBLISHER_INVENTORY &&
                      [6,8,9].includes(columnsValues["inventory_type"]?.value) && 
                        <IntervalAuctionSettings
                          handleColumnChange={handleColumnChange}
                          columnsValues={columnsValues}
                          validationResults={validationResults}
                          setRecurringInterval={setRecurringInterval}
                          recurringInterval={recurringInterval}
                          tableStructure={tableStructure}
                        />
                    }
                    <Col md="12">
                      <div className="d-flex align-items-center justify-content-end">
                        <Button
                          onClick={() => onSubmit()}
                          variant="outline-primary m-4"
                          size="sm"
                        >
                          Create
                        </Button>
                      </div>
                    </Col>
                  </Card.Body>
                </Card>
              </Col>
              {/* Special Case for Publisher Inventory */}
              {table_name === "publisher_inventory" && (
                <Col xs="12" sm="6" lg="3" xl="3">
                  {boxTypes?.includes(1) && (
                    <Card className="mt-4">
                      <Card.Img
                        type="image/svg+xml"
                        src={simpleTopList}
                        variant="top"
                      />
                      <Card.Body>
                        <Card.Title className="text-dark">
                          Simple Top List
                        </Card.Title>
                        <Card.Subtitle className="mb-2 text-muted">
                          1 embed block
                        </Card.Subtitle>
                        <Card.Text>
                          Simple Top List block with custom number of products.
                          Compatible with live or interval auctions.
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  )}
                  {boxTypes?.includes(2) && (
                    <Card className="mt-4">
                      <Card.Img
                        type="image/svg+xml"
                        src={topListOverview}
                        variant="top"
                      />
                      <Card.Body>
                        <Card.Title className="text-dark">
                          Top List + Product Overview
                        </Card.Title>
                        <Card.Subtitle className="mb-2 text-muted">
                          2 embed blocks
                        </Card.Subtitle>
                        <Card.Text>
                          Simple Top List & Overview blocks that stay in sync.
                          Compatible with live or interval auctions.
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  )}
                  {boxTypes?.includes(3) && (
                    <Card className="mt-4">
                      <Card.Img
                        type="image/svg+xml"
                        src={simpleDynamicBanner}
                        variant="top"
                      />
                      <Card.Body>
                        <Card.Title className="text-dark">
                          Simple Dynamic Banner
                        </Card.Title>
                        <Card.Subtitle className="mb-2 text-muted">
                          1 embed block
                        </Card.Subtitle>
                        <Card.Text>
                          Simple Dynamic Banner block. Compatible with live or
                          interval auctions.
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  )}
                  {boxTypes?.includes(4) && (
                    <Card className="mt-4">
                      <Card.Img
                        type="image/svg+xml"
                        src={dynamic3PackBanner}
                        variant="top"
                      />
                      <Card.Body>
                        <Card.Title className="text-dark">
                          Multi-Item Dynamic Banner
                        </Card.Title>
                        <Card.Subtitle className="mb-2 text-muted">
                          1 embed block
                        </Card.Subtitle>
                        <Card.Text>
                          Dynamic Multi-Item Banner blocks. Compatible with live
                          or interval auctions.
                        </Card.Text>
                      </Card.Body>
                    </Card>
                  )}
                </Col>
              )}
            </Row>
          </Col>
        </Row>

      </div>
      {isRecordSubmitting && (
        <LoaderWithBlur
          publisherByAbbr={publisher_id}
        />
      )}
    </React.Fragment>
  );
}
